import { Redirect, RouteProps } from 'react-router-dom';

import { About } from 'src/pages/About';
import { AccountActivated } from 'src/pages/AccountActivated';
import { CommunityDetail } from 'src/components/MobileView/components/CommunityDetail';
import { Company } from 'src/pages/Company';
import { Contact } from 'src/pages/Contact';
import { Embed } from 'src/pages/Embed';
import { HomePage } from 'src/pages/Homepage';
import { Login } from 'src/pages/Login';
import { MailChimp } from 'src/pages/MailChimp';
import { MarketDesign } from 'src/pages/MarketDesign';
import { MarketDesign as MarketDesignMobile } from 'src/pages/MarketDesignMobile';
// import { EcoSystem } from 'src/pages/EcoSystem'; // temporary
import { MyCommunities } from 'src/pages/MyCommunities';
import { PageNotFound } from 'src/pages/PageNotFound';
import { PrivacyPolicy } from 'src/pages/PrivacyPolicy';
import React from 'react';
import { ResetPassword } from 'src/pages/ResetPassword';
import { Resources } from 'src/pages/Resources';
import { SingularityMap } from 'src/pages/SingularityMap';
import { TestPage } from 'src/pages/TestPage';
import { YouAreEnergy } from 'src/pages/YouAreEnergy';

export const routesConfig = {
  home: (): string => '/',
  marketDesign: (): string => '/',
  mailChimp: (): string => '/mail-chimp',
  privacyPolicy: (): string => '/privacy-policy',
  company: (): string => '/company',
  testPage: (): string => '/test-page',
  degreesOfFreedom: (): string => '/degrees-of-freedom',
  singularityMap: (): string => '/singularity-map',
  singularityMapCreate: (): string => '/singularity-map/create',
  singularityMapResults: (configurationUuid?: string, assetUuid?: string): string =>
    `/singularity-map/results/${configurationUuid || ':configurationUuid'}${
      typeof assetUuid === 'string' ? `/${assetUuid}` : '/:assetUuid'
    }`,
  embed: (configurationUuid?: string): string =>
    `/embed/${configurationUuid || ':configurationUuid'}`,
  youAreEnergy: (): string => '/you-are-energy',
  about: (): string => '/about',
  resources: (): string => '/resources',
  contact: (): string => '/contact',
  myCommunities: (): string => '/my-communities',
  confirmEmail: (): string => '/confirm-email/:token',
  accountActivated: (): string => '/account_activated',
  resetPassword: (): string => '/reset/:resetId/:resetKey',
  notFound: (): string => '/404',
  login: (): string => '/login',
};

export const routesTitles: {
  [route in keyof typeof routesConfig]: string;
} = {
  home: '',
  marketDesign: 'Market Design',
  mailChimp: 'Mail Chimp',
  privacyPolicy: 'Privacy Policy',
  testPage: 'Test Page',
  company: 'Company',
  degreesOfFreedom: 'Degrees of Freedom',
  singularityMap: 'Singularity Map',
  singularityMapCreate: 'Singularity Map',
  singularityMapResults: 'Singularity Map',
  youAreEnergy: 'You Are Energy',
  about: 'About',
  resources: 'Resources',
  contact: 'Contact',
  myCommunities: 'My Communities',
  notFound: 'Page Not Found',
  confirmEmail: 'Confirm Email',
  accountActivated: 'Account Activated',
  resetPassword: 'Reset Password',
  login: 'Login',
  embed: 'Singularity Map',
};

export type TRouteNames = keyof typeof routesConfig;

export type TAppRouteProps = {
  private?: boolean;
  routeName: TRouteNames;
} & RouteProps;

const RedirectToMyCommunities = () => {
  return <Redirect to={routesConfig.myCommunities()} />;
};

export const routes: TAppRouteProps[] = [
  {
    routeName: 'testPage',
    path: routesConfig.testPage(),
    component: TestPage,
    private: true,
  },
  {
    routeName: 'home',
    path: routesConfig.marketDesign(),
    component: MarketDesign,
    exact: true,
  },
  {
    routeName: 'mailChimp',
    path: routesConfig.mailChimp(),
    component: MailChimp,
    exact: true,
  },
  {
    routeName: 'privacyPolicy',
    path: routesConfig.privacyPolicy(),
    component: PrivacyPolicy,
    exact: true,
  },
  {
    routeName: 'degreesOfFreedom',
    path: routesConfig.degreesOfFreedom(),
    // component: DegreesOfFreedom,
    component: Contact, // Temporary
    exact: true,
  },
  {
    routeName: 'singularityMap',
    path: routesConfig.singularityMap(),
    component: SingularityMap,
  },
  {
    routeName: 'youAreEnergy',
    path: routesConfig.youAreEnergy(),
    component: YouAreEnergy,
    exact: true,
  },
  {
    routeName: 'about',
    path: routesConfig.about(),
    component: About,
    exact: true,
  },
  {
    routeName: 'company',
    path: `${routesConfig.company()}/:page?/:memberId?`,
    component: Company,
    exact: true,
  },
  {
    routeName: 'resources',
    path: `${routesConfig.resources()}/:page?`,
    component: Resources,
    exact: true,
  },
  {
    routeName: 'contact',
    path: routesConfig.contact(),
    component: Contact,
    exact: true,
  },
  {
    routeName: 'resetPassword',
    path: routesConfig.resetPassword(),
    component: ResetPassword,
    exact: true,
  },
  {
    routeName: 'confirmEmail',
    path: routesConfig.confirmEmail(),
    component: ({ match }: { match: { params: { token: string } } }): null => {
      const endpoint = 'rest-accounts/registration/account-confirm-email/';
      window.location.href = process.env.REACT_APP_D3A_WEB_API_URL + endpoint + match.params.token;
      return null;
    },
    exact: true,
  },
  {
    routeName: 'accountActivated',
    path: routesConfig.accountActivated(),
    component: AccountActivated,
    exact: true,
  },
  {
    routeName: 'embed',
    path: routesConfig.embed(),
    component: Embed,
    exact: true,
  },
  {
    routeName: 'notFound',
    path: '*',
    component: PageNotFound,
  },
];

export const mobileRoutes: TAppRouteProps[] = [
  {
    routeName: 'home',
    path: routesConfig.marketDesign(),
    component: MarketDesignMobile,
    exact: true,
  },
  {
    routeName: 'mailChimp',
    path: routesConfig.mailChimp(),
    component: MailChimp,
    exact: true,
  },
  {
    routeName: 'privacyPolicy',
    path: routesConfig.privacyPolicy(),
    component: PrivacyPolicy,
    exact: true,
  },
  {
    routeName: 'myCommunities',
    path: routesConfig.myCommunities(),
    component: MyCommunities,
    exact: true,
    private: true,
  },
  {
    routeName: 'singularityMap',
    path: routesConfig.singularityMap(),
    component: CommunityDetail,
    private: true,
  },
  {
    routeName: 'login',
    path: routesConfig.login(),
    component: Login,
  },
  {
    routeName: 'company',
    path: `${routesConfig.company()}/:page?/:memberId?`,
    component: Company,
    exact: true,
  },
  {
    routeName: 'resources',
    path: `${routesConfig.resources()}/:page?`,
    component: Resources,
    exact: true,
  },
  {
    routeName: 'marketDesign',
    path: routesConfig.marketDesign(),
    component: MarketDesignMobile,
    exact: true,
  },
];
